/**
 * Dates, relative times and numbers, through Intl only (no hand-written English). Pure: no React, no DOM, so the
 * server, the client and the tests share it.
 *
 * Hydration: the server does not know the browser's time zone, and its locale is only the Accept-Language header.
 * `react.tsx` therefore renders the first pass with `createDateFormat(formatLocale, "UTC")` (the same text on the
 * server and in the browser) and upgrades to the browser's locale and time zone once mounted. The absolute UTC time
 * is always available as a tooltip (`utc`), whatever is shown.
 */

export type DateFormat = {
    /** True once the browser's own locale and time zone are in use; false for the stable server-side render. */
    readonly mounted: boolean;
    /** "11 Oct 2026, 14:02 UTC": absolute, in the format's time zone (the browser's, once mounted). */
    absolute(iso: string): string;
    /** "11 Oct 2026, 14:02:03.123 UTC": to the millisecond, for a value's timestamp, where seconds matter. */
    precise(iso: string): string;
    /** The same instant in UTC, always: the tooltip. */
    utc(iso: string): string;
    /** "14:02": hours and minutes only, in the format's time zone (a "last checked" line). */
    time(iso: string): string;
    /** "5 minutes ago", "now", "2 days ago". Before mount (stable render) it is the absolute time. */
    relative(iso: string, now?: number): string;
    /** "11 Oct 2026, 14:02 UTC (5 minutes ago)"; before mount only the absolute time. */
    both(iso: string, now?: number): string;
};

export type NumberFormat = {
    /** 1234.5 -> "1,234.5" */
    number(value: number, maximumFractionDigits?: number): string;
    /** 0.9987 -> "99.9%" (a fraction, not a percentage) */
    percent(fraction: number, maximumFractionDigits?: number): string;
};

const isValid = (iso: string) => !Number.isNaN(Date.parse(iso));

function safeLocale(locale: string): string {
    try {
        return Intl.getCanonicalLocales(locale)[0] ?? "en-GB";
    } catch {
        return "en-GB";
    }
}

function preciseFormatter(locale: string, timeZone?: string) {
    return new Intl.DateTimeFormat(locale, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        fractionalSecondDigits: 3,
        timeZoneName: "short",
        timeZone,
    });
}

function absoluteFormatter(locale: string, timeZone?: string) {
    return new Intl.DateTimeFormat(locale, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        timeZoneName: "short",
        timeZone,
    });
}

/**
 * Relative thresholds of the old helper, kept: under a minute "now", under an hour in minutes, under 48 hours in
 * hours, then days. A time in the future counts as now.
 */
export function relativeParts(iso: string, now: number): { value: number; unit: Intl.RelativeTimeFormatUnit } {
    const seconds = Math.max(0, Math.round((now - Date.parse(iso)) / 1000));
    if (seconds < 60) return { value: 0, unit: "second" };
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return { value: -minutes, unit: "minute" };
    const hours = Math.floor(minutes / 60);
    if (hours < 48) return { value: -hours, unit: "hour" };
    return { value: -Math.floor(hours / 24), unit: "day" };
}

/**
 * `timeZone` undefined means the runtime's own (the browser's). `mounted: false` is the stable first render:
 * pass "UTC" with it.
 */
export function createDateFormat(locale: string, timeZone?: string, { mounted = true }: { mounted?: boolean } = {}): DateFormat {
    const tag = safeLocale(locale);
    const local = absoluteFormatter(tag, timeZone);
    const utc = absoluteFormatter(tag, "UTC");
    const preciseLocal = preciseFormatter(tag, timeZone);
    const relativeFormat = new Intl.RelativeTimeFormat(tag, { numeric: "auto", style: "long" });
    const timeLocal = new Intl.DateTimeFormat(tag, { hour: "2-digit", minute: "2-digit", ...(timeZone ? { timeZone } : {}) });

    const absolute = (iso: string) => (isValid(iso) ? local.format(Date.parse(iso)) : iso);
    const relative = (iso: string, now: number = Date.now()) => {
        if (!mounted || !isValid(iso)) return absolute(iso);
        const { value, unit } = relativeParts(iso, now);
        return relativeFormat.format(value, unit);
    };
    return {
        mounted,
        absolute,
        precise: (iso) => (isValid(iso) ? preciseLocal.format(Date.parse(iso)) : iso),
        utc: (iso) => (isValid(iso) ? utc.format(Date.parse(iso)) : iso),
        time: (iso) => (isValid(iso) ? timeLocal.format(Date.parse(iso)) : iso),
        relative,
        both: (iso, now) => (mounted && isValid(iso) ? `${absolute(iso)} (${relative(iso, now)})` : absolute(iso)),
    };
}

export function createNumberFormat(locale: string): NumberFormat {
    const tag = safeLocale(locale);
    return {
        number: (value, maximumFractionDigits = 2) => new Intl.NumberFormat(tag, { maximumFractionDigits }).format(value),
        percent: (fraction, maximumFractionDigits = 1) =>
            new Intl.NumberFormat(tag, { style: "percent", maximumFractionDigits }).format(fraction),
    };
}

/**
 * English in UTC, relative times working: the default of the pure helpers (and of tests) that take a `DateFormat`
 * as a parameter. Components pass the one from `useI18n()`.
 */
export const defaultDateFormat: DateFormat = createDateFormat("en-GB", "UTC");
export const defaultNumberFormat: NumberFormat = createNumberFormat("en-GB");
